<template>
  <m-dropdown
    class="menu-list-filter"
    block
    :title="$t('list.filter')"
    :relocate-key="filter.children.length"
    :disabled="disabled && filter.children.length === 0"
  >
    <m-card-item
      icon="funnel"
      :disabled="disabled && filter.children.length === 0"
      @click="handleOpen"
    >
      {{ $t('list.filter') }}
      <template #right>
        <div class="_right">
          <div v-if="filter.children.length > 0">
            {{ filter.children.length }}
          </div>
          <m-icon
            v-if="!(disabled && filter.children.length === 0)"
            type="right"
            :color="$colors.grey.lighten1"
            size="11"
          />
        </div>
      </template>
    </m-card-item>
    <template #overlay>
      <m-card no-padding>
        <filter-options
          v-if="filter.children.length === 0 && openAsOptions"
          :filter-options="scopeItems"
          @add-quick-filter="addQuickFilter"
          @add-advanced-filter="addAdvancedFilter"
        />
        <m-content
          v-else
          :padding-y="6"
          :padding-x="6"
        >
          <filter-chips
            :style="{ minWidth: '30rem' }"
            :props="filterProps"
            :filter="filter"
            :quick-filters="quickFilters"
            :has-dynamic-dates="hasDynamicDateFilters"
            :unsaved-changes="unsavedChanges"
            :multi-model="multiModel"
            :disabled="disabled"
            class="_filter-chips"
            layout="vertical"
            @update:filter="updateViewParams($event, 'filter')"
            @update:quick-filters="updateViewParams($event, 'quickFilters')"
          />
          <template v-if="filter.children.length > 0 && showApplyFilterOnFirstLevelOnly">
            <m-divider xxs />
            <apply-filter-on-first-level-only
              :value="currentView.params?.applyFilterOnFirstLevelOnly"
              :disabled="disabled"
              @update:value="updateViewParams($event, 'applyFilterOnFirstLevelOnly')"
            />
          </template>
          <template v-if="!disabled && hasUnsavedFilterChanges">
            <m-divider xxs />
            <save-view-actions
              :views-service="viewsService"
              layout="vertical"
              :only-params="onlyParams"
            />
          </template>
        </m-content>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import ApplyFilterOnFirstLevelOnly from '@/components/filter/ApplyFilterOnFirstLevelOnly.vue';
import FilterChips from '@/components/filter/FilterChips.vue';
import FilterOptions from '@/components/filter/FilterOptions.vue';
import SaveViewActions from '@/components/list/SaveViewActions.vue';
import useScopeFilter from '@/composables/filter/scope-filter';
import useScopeItems from '@/composables/filter/scope-items';
import { computed, ref } from 'vue';
import { isValidTree } from '@/lib/filter/scope-tree';

const props = defineProps({
  filterProps: {
    type: Array,
    default: () => [],
  },
  hasDynamicDateFilters: {
    type: Boolean,
    default: false,
  },
  multiModel: {
    type: Boolean,
    default: false,
  },
  viewsService: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showApplyFilterOnFirstLevelOnly: {
    type: Boolean,
    default: true,
  },
});

const openAsOptions = ref(false);

const handleOpen = () => {
  if (filter.value.children.length === 0) {
    openAsOptions.value = true;
  }
};

const onlyParams = ['filter', 'quickFilters', 'applyFilterOnFirstLevelOnly'];
const { currentView } = props.viewsService;
// Chip markers only show for views the user can save, the save actions do not depend
// on that because their reset is the only way to drop a local overwrite.
const unsavedChanges = computed(() => (currentView.value.canEdit ? props.viewsService.currentViewUnsavedChanges.value : []));
const hasUnsavedFilterChanges = computed(() => onlyParams.some((param) => props.viewsService.currentViewUnsavedChanges.value.includes(param)));

const filter = computed(() => currentView.value.params?.filter ?? null);
const quickFilters = computed(() => currentView.value.params?.quickFilters ?? []);

const updateViewParams = (value, key) => {
  props.viewsService.setParams(
    currentView.value,
    {
      ...currentView.value.params,
      [key]: value,
    },
  );
};

const { scopeItems } = useScopeItems(
  computed(() => props.filterProps.filter((p) => !p.property.noFilter)),
  false,
  props.multiModel,
);
const {
  unusedScopeItems,
  buildNode,
  buildFilterWithNewNode,
} = useScopeFilter(scopeItems, filter, quickFilters);

const addQuickFilter = (scopeItem) => {
  openAsOptions.value = false;
  const { filter, newGroup } = buildFilterWithNewNode(buildNode(scopeItem));
  if (!isValidTree(filter)) {
    return;
  }
  updateViewParams(filter, 'filter');
  updateViewParams([newGroup.key], 'quickFilters');
};

const firstUnusedScopeItem = computed(() => {
  if (unusedScopeItems.value.length === 0) {
    return null;
  }
  return unusedScopeItems.value[0];
});

const addAdvancedFilter = () => {
  openAsOptions.value = false;
  const { filter } = buildFilterWithNewNode(buildNode(firstUnusedScopeItem.value));
  if (!isValidTree(filter)) {
    return;
  }
  updateViewParams(filter, 'filter');
};

</script>

<style scoped lang="scss">
.menu-list-filter {
  ._right {
    display: flex;
    gap: .4rem;
    color: $font-color-secondary
  }
}
</style>
